import { Vec2 } from '../../math/vec2.js';
import { SeatingElement } from './seating-element.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { cloneList, isSameData, isSameList, SerializedData } from '../../dto/dto.js';
import { isNotNullOrUndefined, isNullOrUndefined } from '../../../utility/null-safety.js';

export class Seat extends SeatingElement {
  static readonly TYPE_ID: number = 0x01;

  public readonly tickets: string[];

  private constructor(
      uniqueId: number,
      enabled: boolean,
      position: Vec2,
      rotation: number,
      tickets: string[],
      parent: WeakRef<SeatingElement> | null = null,
  ) {
    super(uniqueId, enabled, position, rotation, parent);
    this.tickets = tickets;
  }

  get $typeId(): number {
    return Seat.TYPE_ID;
  }

  static legacy(
      uniqueId: number,
      enabled: boolean,
      position: Vec2,
      rotation: number,
      ticketTemplateId: string | null,
  ): Seat {
    return new Seat(
        uniqueId,
        enabled,
        position,
        rotation,
        isNullOrUndefined(ticketTemplateId) ? [] : [ticketTemplateId],
        null,
    );
  }

  static blank(
      uniqueId: number,
      enabled: boolean,
      position: Vec2,
      rotation: number,
  ): Seat {
    return new Seat(
        uniqueId,
        enabled,
        position,
        rotation,
        [],
        null,
    );
  }

  static fromBinaryData(
      uniqueId: number,
      enabled: boolean,
      position: Vec2,
      rotation: number,
      tickets: string[],
      parent: WeakRef<SeatingElement> | null = null,
  ): Seat {
    return new Seat(
        uniqueId,
        enabled,
        position,
        rotation,
        tickets,
        parent,
    );
  }

  static fromJson(data: SerializedData): Seat {
    const ticketTemplateId = MapUtils.getOrNull<string>(data, 'ticketTemplateId');
    let tickets = MapUtils.getListOrNull<string>(data, 'tickets', (v) => v as string);
    if(isNullOrUndefined(tickets)) {
      tickets = isNotNullOrUndefined(ticketTemplateId) ? [ticketTemplateId] : [];
    }

    return new Seat(
        MapUtils.get<number>(data, 'uniqueId'),
        MapUtils.get<boolean>(data, 'enabled'),
        Vec2.fromJson(MapUtils.get<SerializedData>(data, 'position')),
        MapUtils.getNumber(data, 'rotation'),
        tickets,
    );
  }

  toJson(): SerializedData {
    return {
      '$typeId': this.$typeId,
      'uniqueId': this.uniqueId,
      'enabled': this.enabled,
      'position': this.position.toJson(),
      'rotation': this.rotation,
      'tickets': this.tickets,
    };
  }

  clone(parent: WeakRef<SeatingElement> | null): this {
    return new Seat(
        this.uniqueId,
        this.enabled,
        this.position.clone(),
        this.rotation,
        cloneList(this.tickets),
        parent,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return this.uniqueId === other.uniqueId &&
        this.enabled === other.enabled &&
        isSameData(this.position, other.position) &&
        this.rotation === other.rotation &&
        isSameList(other.tickets, this.tickets);
  }
}
